import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = { primary: '#2b3380', success: '#4caf50', warning: '#ff9800', error: '#f44336' };

function EmailManagement({ user, onLogout }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showBulkCreate, setShowBulkCreate] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setLoading(false);
    }
  };

  const generateEmail = (firstName, lastName) => {
    const first = firstName.toLowerCase().replace(/[^a-z]/g, '');
    const last = lastName.toLowerCase().replace(/[^a-z]/g, '');
    return `${first}.${last}@zobox.in`;
  };

  const handleCreateEmail = async (employeeId, name) => {
    const names = name.split(' ');
    const firstName = names[0] || '';
    const lastName = names[names.length - 1] || names[0];
    const email = generateEmail(firstName, lastName);

    if (!window.confirm(`Create email: ${email} for ${name}?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/employees/${employeeId}`,
        { email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Email created: ${email}`);
      fetchEmployees();
    } catch (error) {
      console.error('Error creating email:', error);
      alert(error.response?.data?.detail || 'Failed to create email');
    }
  };

  const handleBulkCreate = async () => {
    const employeesWithoutEmail = employees.filter(e => !e.email);
    
    if (employeesWithoutEmail.length === 0) {
      alert('All employees already have emails!');
      return;
    }

    if (!window.confirm(`Create emails for ${employeesWithoutEmail.length} employees?`)) return;

    setCreating(true);
    let success = 0;
    let failed = 0;

    for (const emp of employeesWithoutEmail) {
      try {
        const names = emp.name.split(' ');
        const firstName = names[0] || '';
        const lastName = names[names.length - 1] || names[0];
        const email = generateEmail(firstName, lastName);

        const token = localStorage.getItem('token');
        await axios.put(
          `${API}/api/employees/${emp.id}`,
          { email },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        success++;
      } catch (error) {
        console.error(`Failed for ${emp.name}:`, error);
        failed++;
      }
    }

    alert(`Bulk create complete!\nSuccess: ${success}\nFailed: ${failed}`);
    setCreating(false);
    fetchEmployees();
    setShowBulkCreate(false);
  };

  const employeesWithEmail = employees.filter(e => e.email);
  const employeesWithoutEmail = employees.filter(e => !e.email);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold" style={{ color: COLORS.primary }}>📧 Email Management</h1>
          <p className="mt-2 text-gray-600">Create and manage employee email addresses (firstname.lastname@zobox.in)</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Employees</p>
                <h2 className="text-4xl font-bold mt-2" style={{ color: COLORS.primary }}>{employees.length}</h2>
              </div>
              <div className="text-5xl">👥</div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">With Email</p>
                <h2 className="text-4xl font-bold mt-2" style={{ color: COLORS.success }}>{employeesWithEmail.length}</h2>
              </div>
              <div className="text-5xl">✅</div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Without Email</p>
                <h2 className="text-4xl font-bold mt-2" style={{ color: COLORS.warning }}>{employeesWithoutEmail.length}</h2>
              </div>
              <div className="text-5xl">⚠️</div>
            </div>
          </div>
        </div>

        {/* Bulk Create Button */}
        {employeesWithoutEmail.length > 0 && (
          <div className="mb-6">
            <button
              onClick={() => setShowBulkCreate(true)}
              className="px-6 py-3 text-white rounded-lg font-medium hover:opacity-90"
              style={{ backgroundColor: COLORS.primary }}
              disabled={creating}
            >
              {creating ? 'Creating...' : `🚀 Bulk Create Emails (${employeesWithoutEmail.length})`}
            </button>
          </div>
        )}

        {/* Employees Without Email */}
        {employeesWithoutEmail.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg mb-6">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-xl font-bold" style={{ color: COLORS.warning }}>⚠️ Employees Without Email</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Suggested Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {employeesWithoutEmail.map((emp) => {
                    const names = emp.name.split(' ');
                    const suggestedEmail = generateEmail(names[0] || '', names[names.length - 1] || names[0]);
                    
                    return (
                      <tr key={emp.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{emp.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{emp.employee_id}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{emp.designation}</td>
                        <td className="px-6 py-4 text-sm text-blue-600 font-mono">{suggestedEmail}</td>
                        <td className="px-6 py-4 text-sm">
                          <button
                            onClick={() => handleCreateEmail(emp.id, emp.name)}
                            className="px-4 py-2 text-white rounded hover:opacity-90"
                            style={{ backgroundColor: COLORS.success }}
                          >
                            Create Email
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Employees With Email */}
        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-xl font-bold" style={{ color: COLORS.success }}>✅ Employees With Email</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {employeesWithEmail.map((emp) => (
                  <tr key={emp.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{emp.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{emp.employee_id}</td>
                    <td className="px-6 py-4 text-sm text-blue-600 font-mono">{emp.email}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{emp.designation}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full" style={{ backgroundColor: '#e8f5e9', color: COLORS.success }}>
                        Active
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bulk Create Confirmation Modal */}
      {showBulkCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <h2 className="text-2xl font-bold mb-4" style={{ color: COLORS.primary }}>Bulk Create Emails</h2>
            <p className="text-gray-700 mb-4">
              This will create email addresses for {employeesWithoutEmail.length} employees using the format:
            </p>
            <p className="text-center font-mono text-lg mb-4" style={{ color: COLORS.primary }}>
              firstname.lastname@zobox.in
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-4 max-h-60 overflow-y-auto">
              <p className="font-semibold mb-2">Preview:</p>
              {employeesWithoutEmail.slice(0, 10).map((emp) => {
                const names = emp.name.split(' ');
                const email = generateEmail(names[0] || '', names[names.length - 1] || names[0]);
                return (
                  <p key={emp.id} className="text-sm text-gray-700">
                    {emp.name} → <span className="text-blue-600 font-mono">{email}</span>
                  </p>
                );
              })}
              {employeesWithoutEmail.length > 10 && (
                <p className="text-sm text-gray-500 mt-2">...and {employeesWithoutEmail.length - 10} more</p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBulkCreate(false)}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                disabled={creating}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkCreate}
                className="px-6 py-2 text-white rounded-lg font-medium hover:opacity-90"
                style={{ backgroundColor: COLORS.primary }}
                disabled={creating}
              >
                {creating ? 'Creating...' : 'Create All Emails'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default EmailManagement;
